import React, { useState, useEffect, useCallback, useMemo } from 'react';
import walletService from '../services/walletService';
import '../styles/wallet.css';

const Wallet = () => {
    const [wallet, setWallet] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [balance, setBalance] = useState(0);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [filter, setFilter] = useState({ type: '', transaction_type: '' });
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

    const fetchWalletDetails = useCallback(async () => {
        const result = await walletService.getWallet(pagination.limit, (pagination.page - 1) * pagination.limit);
        if (result.success) {
            setWallet(result.data.wallet);
            setBalance(result.data.wallet?.balance || 0);
        }
    }, [pagination.limit, pagination.page]);

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        const result = await walletService.getTransactions({
            ...filter,
            limit: pagination.limit,
            offset: (pagination.page - 1) * pagination.limit
        });

        if (result.success) {
            setTransactions(result.data);
            setPagination((prev) => ({ ...prev, total: result.total }));
        }

        setLoading(false);
    }, [filter, pagination.limit, pagination.page]);

    useEffect(() => {
        fetchWalletDetails();
        fetchTransactions();
    }, [fetchTransactions, fetchWalletDetails]);

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

    const formatPrice = (price) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price || 0);

    const getTransactionIcon = (type, transactionType) => {
        if (type === 'credit') {
            if (transactionType === 'reward') return 'fas fa-gift';
            if (transactionType === 'refund') return 'fas fa-rotate-left';
            if (transactionType === 'bonus') return 'fas fa-plus';
            return 'fas fa-arrow-down';
        }

        return 'fas fa-arrow-up';
    };

    const getTransactionColor = (type) => (type === 'credit' ? 'credit' : 'debit');

    const walletInsights = useMemo(() => {
        const rewardCount = transactions.filter((tx) => tx.transaction_type === 'reward').length;
        const refundCount = transactions.filter((tx) => tx.transaction_type === 'refund').length;
        const completedCount = transactions.filter((tx) => tx.status === 'completed').length;

        return [
            {
                label: 'Reward Entries',
                value: rewardCount,
                icon: 'fas fa-gift'
            },
            {
                label: 'Refund Entries',
                value: refundCount,
                icon: 'fas fa-rotate-left'
            },
            {
                label: 'Completed Transactions',
                value: completedCount,
                icon: 'fas fa-check-circle'
            }
        ];
    }, [transactions]);

    if (loading) {
        return (
            <div className="wallet-loading">
                <div className="wallet-spinner"></div>
                <p>Loading your wallet dashboard...</p>
            </div>
        );
    }

    return (
        <div className="wallet-page">
            <section className="wallet-hero">
                <div className="wallet-hero-content">
                    <span className="wallet-badge">RedGoTrip Wallet</span>
                    <h1>Wallet & Rewards</h1>
                    <p>Track your travel rewards, refunds and wallet usage from one clean dashboard.</p>
                </div>
            </section>

            <div className="wallet-container">
                <section className="wallet-summary-grid">
                    <div className="wallet-balance-card">
                        <div className="wallet-balance-top">
                            <div className="wallet-balance-icon">
                                <i className="fas fa-wallet"></i>
                            </div>
                            <div className="wallet-balance-copy">
                                <span className="wallet-balance-label">Available Balance</span>
                                <strong className="wallet-balance-amount">{formatPrice(balance)}</strong>
                                <p>Use your wallet during checkout for package and camping bookings.</p>
                            </div>
                        </div>

                        <div className="wallet-balance-footer">
                            <div className="wallet-balance-mini">
                                <span>Total Credited</span>
                                <strong>{formatPrice(wallet?.total_credited)}</strong>
                            </div>
                            <div className="wallet-balance-mini">
                                <span>Total Debited</span>
                                <strong>{formatPrice(wallet?.total_debited)}</strong>
                            </div>
                            <button className="wallet-bonus-btn">
                                <i className="fas fa-gift"></i>
                                Add Bonus
                            </button>
                        </div>
                    </div>

                    <div className="wallet-side-stats">
                        <div className="wallet-stat-card">
                            <div className="wallet-stat-icon credited">
                                <i className="fas fa-arrow-down"></i>
                            </div>
                            <div>
                                <span className="wallet-stat-label">Total Credited</span>
                                <strong className="wallet-stat-value">{formatPrice(wallet?.total_credited)}</strong>
                            </div>
                        </div>

                        <div className="wallet-stat-card">
                            <div className="wallet-stat-icon debited">
                                <i className="fas fa-arrow-up"></i>
                            </div>
                            <div>
                                <span className="wallet-stat-label">Total Debited</span>
                                <strong className="wallet-stat-value">{formatPrice(wallet?.total_debited)}</strong>
                            </div>
                        </div>

                        {walletInsights.map((item, index) => (
                            <div key={index} className="wallet-stat-card compact">
                                <div className="wallet-stat-icon neutral">
                                    <i className={item.icon}></i>
                                </div>
                                <div>
                                    <span className="wallet-stat-label">{item.label}</span>
                                    <strong className="wallet-stat-value">{item.value}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <div className="wallet-tabs">
                    <button
                        className={`wallet-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <i className="fas fa-grid-2"></i>
                        Overview
                    </button>
                    <button
                        className={`wallet-tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
                        onClick={() => setActiveTab('transactions')}
                    >
                        <i className="fas fa-clock-rotate-left"></i>
                        Transactions
                    </button>
                </div>

                {activeTab === 'overview' && (
                    <div className="wallet-overview-grid">
                        <div className="wallet-info-card">
                            <div className="wallet-card-head">
                                <h3>How to earn wallet rewards</h3>
                                <span>Smart ways to grow your balance</span>
                            </div>
                            <ul className="wallet-points-list">
                                <li><i className="fas fa-suitcase"></i> Book any package and earn travel rewards.</li>
                                <li><i className="fas fa-user-friends"></i> Refer friends and unlock wallet bonuses.</li>
                                <li><i className="fas fa-badge-percent"></i> Get cashback on selected seasonal deals.</li>
                                <li><i className="fas fa-cake-candles"></i> Enjoy special bonus campaigns and festive offers.</li>
                            </ul>
                        </div>

                        <div className="wallet-info-card">
                            <div className="wallet-card-head">
                                <h3>How to use your wallet</h3>
                                <span>Simple and secure at checkout</span>
                            </div>
                            <ul className="wallet-points-list">
                                <li><i className="fas fa-credit-card"></i> Apply wallet balance during package or camping checkout.</li>
                                <li><i className="fas fa-layer-group"></i> Combine wallet money with other payment methods if needed.</li>
                                <li><i className="fas fa-bolt"></i> Wallet deduction is instant once payment is confirmed.</li>
                                <li><i className="fas fa-shield-halved"></i> Refunds and credits are tracked securely in one place.</li>
                            </ul>
                        </div>

                        <div className="wallet-info-card recent">
                            <div className="wallet-card-head">
                                <h3>Recent activity</h3>
                                <span>Your latest wallet movement</span>
                            </div>

                            <div className="wallet-recent-list">
                                {transactions.slice(0, 5).map((transaction) => (
                                    <div key={transaction.id} className="wallet-transaction-row">
                                        <div className={`wallet-transaction-icon ${getTransactionColor(transaction.type)}`}>
                                            <i className={getTransactionIcon(transaction.type, transaction.transaction_type)}></i>
                                        </div>
                                        <div className="wallet-transaction-copy">
                                            <strong>{transaction.description}</strong>
                                            <span>{formatDate(transaction.created_at)}</span>
                                        </div>
                                        <div className={`wallet-transaction-amount ${getTransactionColor(transaction.type)}`}>
                                            {transaction.type === 'credit' ? '+' : '-'} {formatPrice(transaction.amount)}
                                        </div>
                                    </div>
                                ))}

                                {transactions.length === 0 && (
                                    <div className="wallet-empty-state">
                                        <i className="fas fa-receipt"></i>
                                        <p>No transactions yet</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'transactions' && (
                    <div className="wallet-transactions-panel">
                        <div className="wallet-filter-bar">
                            <div className="wallet-filter-group">
                                <label>Flow</label>
                                <select
                                    value={filter.type}
                                    onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                                >
                                    <option value="">All Types</option>
                                    <option value="credit">Credit</option>
                                    <option value="debit">Debit</option>
                                </select>
                            </div>

                            <div className="wallet-filter-group">
                                <label>Category</label>
                                <select
                                    value={filter.transaction_type}
                                    onChange={(e) => setFilter({ ...filter, transaction_type: e.target.value })}
                                >
                                    <option value="">All Transactions</option>
                                    <option value="reward">Reward</option>
                                    <option value="refund">Refund</option>
                                    <option value="bonus">Bonus</option>
                                    <option value="payment">Payment</option>
                                </select>
                            </div>

                            <button
                                onClick={() => setFilter({ type: '', transaction_type: '' })}
                                className="wallet-clear-btn"
                            >
                                Clear Filters
                            </button>
                        </div>

                        <div className="wallet-transactions-list">
                            {transactions.map((transaction) => (
                                <div key={transaction.id} className="wallet-transaction-card">
                                    <div className="wallet-transaction-top">
                                        <span className="wallet-transaction-id">{transaction.transaction_id}</span>
                                        <span className={`wallet-status-badge ${transaction.status}`}>
                                            {transaction.status}
                                        </span>
                                    </div>

                                    <div className="wallet-transaction-main">
                                        <div className={`wallet-transaction-icon large ${getTransactionColor(transaction.type)}`}>
                                            <i className={getTransactionIcon(transaction.type, transaction.transaction_type)}></i>
                                        </div>

                                        <div className="wallet-transaction-content">
                                            <strong>{transaction.description}</strong>
                                            <div className="wallet-transaction-meta">
                                                <span><i className="fas fa-tag"></i> {transaction.transaction_type}</span>
                                                <span><i className="far fa-clock"></i> {formatDate(transaction.created_at)}</span>
                                            </div>
                                        </div>

                                        <div className={`wallet-transaction-amount large ${getTransactionColor(transaction.type)}`}>
                                            {transaction.type === 'credit' ? '+' : '-'} {formatPrice(transaction.amount)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {transactions.length === 0 && (
                            <div className="wallet-empty-state panel">
                                <i className="fas fa-receipt"></i>
                                <p>No transactions found</p>
                            </div>
                        )}

                        {pagination.total > pagination.limit && (
                            <div className="wallet-pagination">
                                <button
                                    disabled={pagination.page === 1}
                                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                </button>
                                <span>Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}</span>
                                <button
                                    disabled={pagination.page === Math.ceil(pagination.total / pagination.limit)}
                                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                                >
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Wallet;
